import type { ListFilterInputTypeOfWorkOrderChangeSeasonPriceDataFilterInput } from 'gql/graphql';

import * as R from 'ramda';

type TFilterArr = string[];

interface IFilters {
    code?: TFilterArr;
    name?: TFilterArr;
    groupName?: TFilterArr;
    sectionName?: TFilterArr;
    searchString?: string;
}

const getFilterArray = (arr: TFilterArr) =>
    arr.reduce(
        (
            acc: {
                or: { eq: string }[];
            },
            item,
        ) => {
            acc.or.push({ eq: item });

            return acc;
        },
        { or: [] },
    );

export const getWhere = <Result>(filters: IFilters, isRatio = false) => {
    const { code, name, groupName, sectionName, searchString } = filters;

    const filtersWhere = R.pickBy(R.identity)({
        code: code && getFilterArray(code),
        name: name && !isRatio && getFilterArray(name),
        groupName: groupName && getFilterArray(groupName),
        sectionName: sectionName && getFilterArray(sectionName),
        workConditions: name && isRatio && getFilterArray(name),
    }) as Result;

    const stringWhere = searchString && {
        or: [
            {
                code: { containsi: searchString },
            },
            {
                name: {
                    containsi: searchString,
                },
            },
            {
                groupName: { containsi: searchString },
            },
            {
                sectionName: {
                    containsi: searchString,
                },
            },
        ],
    };

    if (stringWhere && !R.isEmpty(filtersWhere)) {
        return {
            and: [stringWhere, filtersWhere],
        };
    }

    if (stringWhere) {
        return stringWhere;
    }

    if (!R.isEmpty(filtersWhere)) {
        return filtersWhere;
    }

    return undefined;
};

const renameKeysInObj = (keysMap: Record<string, string>) => (objArr: Array<Record<string, any>>) =>
    objArr.map((obj) =>
        Object.entries(obj).reduce((a, [k, v]) => (k in keysMap ? { ...a, [keysMap[k]]: v } : { ...a, [k]: v }), {}),
    );

const workOrderChangeVsWorkOrderKeyMap = {
    otherCostId: 'contractOtherId',
    objectIdList: 'objectIdsList',
};

export const renameVariablesForWorkOrderChange = renameKeysInObj(workOrderChangeVsWorkOrderKeyMap);

export const getWhereFromFilter = (fieldName: string, valuesArr: string[], restData: any[] | null) => {
    if (!valuesArr.length) {
        if (fieldName === 'code') {
            return { or: undefined };
        }

        return { [fieldName]: undefined };
    }

    if (fieldName === 'code' && restData?.length) {
        return {
            or: restData.map(({ data }: { data: { isParent: boolean; parentCode: string; code: string } }) => ({
                and: [
                    { uerCode: { eq: data.parentCode } },
                    {
                        or: [
                            { code: { eq: data.isParent ? data.parentCode : data.code } },
                            { code: { eq: data.parentCode } },
                        ],
                    },
                ],
            })),
        };
    }

    if (fieldName === 'status') {
        return {
            or: valuesArr.map((item) => ({
                status: {
                    eq: item,
                },
            })),
        };
    }

    const seasonsKeys = ['planVolume_', 'completedVolume_', 'remainderVolume_'];

    if (
        fieldName.startsWith(seasonsKeys[0]) ||
        fieldName.startsWith(seasonsKeys[1]) ||
        fieldName.startsWith(seasonsKeys[2])
    ) {
        const keySplitted = fieldName.split('_');
        const field = keySplitted[0];
        const year = Number(keySplitted[1]);

        const seasonPrices: ListFilterInputTypeOfWorkOrderChangeSeasonPriceDataFilterInput = {
            all: {
                [field]: { in: valuesArr },
                year: { eq: year },
            },
        };

        return {
            seasonPrices,
        };
    }

    return {
        [fieldName]: {
            or: valuesArr.map((item: string) => ({
                eq: item,
            })),
        },
    };
};
